<template>
  <div class="payment-methods">
    <!-- title
    <h3>
      Formas de pagamento R$: <strong>{{ formatCurrency(ticketPrice) }}</strong>
    </h3>

        <p class="ticket-price">
        </p>
    -->

    <div
      v-for="method in methods"
      :key="method.key"
      class="payment-method"
    >
      <b-form-checkbox v-model="method.enabled" @change="onToggle(method)">
        {{ method.label }}
      </b-form-checkbox>
      <CurrencyBaseInput
        :value="method.amount"
        :model-value="method.amount"
        :max="remainingFor(method)"
        :disabled="!method.enabled"
        :options="currencyOptions"
        placeholder="R$ 0,00"
        @input="onAmountInput(method, $event)"
      />
    </div>

    <div class="summary pt-4 p-block w-100 text-right">
      <span v-if="!isValid" class="text-right w-100 pb-4 d-block">
        *<sub>total pendente </sub>
        <span class="error">{{ formatCurrency(pendingAmount) }}</span>
        <sub> de {{ formatCurrency(ticketPrice) }}</sub>
      </span>
      <span v-else class="text-right text-success w-100 pb-4 d-block">
        *reserva paga: <b>{{ formatCurrency(ticketPrice) }}</b>
      </span>
    </div>
  </div>
</template>

<script setup>
import { reactive, computed, watch } from 'vue'
import store from '@/store'
import CurrencyBaseInput from '@/components/Inputs/CurrencyBaseInput.vue'

const props = defineProps({
  ticketPrice: {
    type: Number,
    required: true,
  },
})

const emit = defineEmits(['update', 'validation'])

const knownPaymentKeys = {
  dinheiro: 'cod_money',
  'cartao de credito': 'cod_credit',
  'cartao de debito': 'cod_debit',
  pix: 'cod_pix',
  cheque: 'cod_cheque',
  transferencia: 'cod_transfer',
  boleto: 'cod_boleto',
}

function paymentKey(name) {
  const normalized = String(name || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
  if (knownPaymentKeys[normalized]) return knownPaymentKeys[normalized]
  const slug = normalized.replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')
  return slug ? `cod_${slug}` : 'cod'
}

function blankMethod(key, label) {
  return { key, label, enabled: false, amount: 0 }
}

const currencyOptions = {
  locale: 'pt-BR',
  currency: 'BRL',
  currencyDisplay: 'symbol',
  precision: 2,
  hideCurrencySymbolOnFocus: false,
  hideGroupingSeparatorOnFocus: false,
  hideNegligibleDecimalDigitsOnFocus: false,
  autoDecimalDigits: true,
  useGrouping: true,
  accountingSign: false,
}

function toCents(value) {
  const number = Number(value)
  if (!Number.isFinite(number)) return 0
  return Math.round(number * 100)
}

function methodsFromPlace() {
  const user = store.getters['auth/user']
  const source = (user && user.paymentMethods) || {}
  const entries = Object.entries(source)
    .filter(([index, name]) => String(index) !== '0' && name)
    .sort((left, right) => Number(left[0]) - Number(right[0]))
  if (!entries.length) {
    return [
      blankMethod('cod_credit', 'Cartão de crédito'),
      blankMethod('cod_debit', 'Cartão de débito'),
      blankMethod('cod_money', 'Dinheiro'),
      blankMethod('cod_pix', 'Pix'),
    ]
  }
  return entries.map((entry) => blankMethod(paymentKey(entry[1]), entry[1]))
}

const methods = reactive(methodsFromPlace())

/* -----------------------------
   Currency Formatter
----------------------------- */
const formatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
})

function formatCurrency(value) {
  return formatter.format(value || 0)
}

/* -----------------------------
   Computed values
----------------------------- */
const totalPaidCents = computed(() => {
  return methods.reduce((sum, method) => {
    if (!method.enabled) return sum
    return sum + toCents(method.amount)
  }, 0)
})

const pendingAmount = computed(
  () => (toCents(props.ticketPrice) - totalPaidCents.value) / 100
)

const isValid = computed(() => {
  return totalPaidCents.value === toCents(props.ticketPrice)
})

function onToggle(method) {
  if (!method.enabled) method.amount = 0
}

function remainingFor(method) {
  const others = methods.reduce((sum, item) => {
    if (item === method || !item.enabled) return sum
    return sum + toCents(item.amount)
  }, 0)
  return Math.max(toCents(props.ticketPrice) - others, 0) / 100
}

function onAmountInput(method, value) {
  const others = methods.reduce((sum, item) => {
    if (item === method || !item.enabled) return sum
    return sum + toCents(item.amount)
  }, 0)
  const remaining = Math.max(toCents(props.ticketPrice) - others, 0)
  const next = Math.min(Math.max(toCents(value), 0), remaining)
  method.amount = next / 100
}

/* -----------------------------
   Emit data
----------------------------- */
watch(
  methods,
  () => {
    emit(
      'update',
      methods
        .filter((m) => m.enabled)
        .map((m) => ({
          method: m.key,
          amount: m.amount,
          label: m.label,
        }))
    )

    emit('validation', isValid.value)
  },
  { deep: true }
)
</script>

<style scoped>
.xpayment-methods {
  max-width: 480px;
}

.xpayment-method {
  display: grid;
  grid-template-columns: 1fr 120px 120px;
  gap: 8px;
  margin-bottom: 8px;
}

.payment-method {
  margin-bottom: 1rem;
}
.payment-method .form-control:enabled {
  border: 1px solid #12576e;
}

.ticket-price {
  margin-bottom: 12px;
}

.formatted {
  font-size: 0.85rem;
  color: #666;
}

.summary {
  margin-top: 12px;
}

.error {
  color: #d00;
  font-weight: bold;
}
</style>
